import { ModuleWithProviders, NgModule } from '@angular/core';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { RouterModule } from '@angular/router';
import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { NgbModule, NgbDateParserFormatter, NgbDateAdapter } from '@ng-bootstrap/ng-bootstrap';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { FormlyModule } from '@ngx-formly/core';
import { FormlyBootstrapModule } from '@ngx-formly/bootstrap';

import {
  // Forms
  AppFormComponent,
  FormFieldDirective,
  FormButtonComponent,
  FormButtonGroupComponent,
  FormInputComponent,
  FormFileComponent,
  FormFilePathComponent,
  FormDateComponent,
  FormTimeComponent,
  FormTextareaComponent,
  FormCheckboxComponent,
  FormCheckboxListComponent,
  FormRadioListComponent,
  FormSelectComponent,
  FormFieldErrorComponent,
  FormLinkComponent,
  FormsService,
  // Custom components
  AccordionComponent,
  PageHeadingComponent,
  CardDeckComponent,
  CheckboxListComponent,
  CardComponent,
  ToggleSwitchComponent,
  SearchInputComponent,
  ListComponent,
  FormInputGroupComponent,
  ModalComponent,
  ModalTemplateDirective,
  ToastComponent,
  AppFileInputDirective,
  LoadingComponent,
  LoginComponent,
  LogoutComponent,
  LoginMenuComponent,
  RadioButtonsComponent,
  RadioListComponent,
  GreaterThanValidatorDirective,
  TooltipComponent,
  CurrencyMaskDirective,
  DropdownFilterSorterComponent,
} from './components';
import { MssLibConfig, MSSLIB_CONFIG } from './models';
import { GroupByPipe, SafePipe, YesNoPipe } from './pipes';
import {
  CustomDateFormatter, CustomNgbDateNativeUTCAdapter, AuthInterceptor, LoadingInterceptor, JwtInterceptor,
} from './services';
import { routes } from './msslib.routes';

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgbModule,
    DragDropModule,
    FormlyModule,
    FormlyBootstrapModule,
    RouterModule.forChild(routes),
    // Custom components
    AccordionComponent,
    PageHeadingComponent,
    CardDeckComponent,
    CheckboxListComponent,
    DropdownFilterSorterComponent,
    CardComponent,
    ToggleSwitchComponent,
    SearchInputComponent,
    ListComponent,
    LoginComponent,
    LogoutComponent,
    LoginMenuComponent,
    // Forms
    // Directives
    CurrencyMaskDirective,
    FormFieldDirective,
    GreaterThanValidatorDirective,
    // Components
    AppFormComponent,
    FormButtonComponent,
    FormButtonGroupComponent,
    FormInputComponent,
    FormInputGroupComponent,
    FormFileComponent,
    AppFileInputDirective,
    FormFilePathComponent,
    FormDateComponent,
    FormTimeComponent,
    FormTextareaComponent,
    FormCheckboxComponent,
    FormCheckboxListComponent,
    FormRadioListComponent,
    FormSelectComponent,
    FormFieldErrorComponent,
    FormLinkComponent,
    ModalComponent,
    ModalTemplateDirective,
    ToastComponent,
    LoadingComponent,
    RadioButtonsComponent,
    RadioListComponent,
    TooltipComponent,
    // Pipes
    SafePipe,
    GroupByPipe,
    YesNoPipe,
  ],
  exports: [
    // Modules
    CommonModule,
    NgbModule,
    ReactiveFormsModule,
    FormsModule,
    DragDropModule,
    FormlyModule,
    FormlyBootstrapModule,
    // Custom components
    LoginComponent,
    LogoutComponent,
    LoginMenuComponent,
    AccordionComponent,
    PageHeadingComponent,
    CardDeckComponent,
    CheckboxListComponent,
    DropdownFilterSorterComponent,
    CardComponent,
    ToggleSwitchComponent,
    SearchInputComponent,
    ListComponent,
    // Forms
    AppFormComponent,
    FormButtonComponent,
    FormButtonGroupComponent,
    FormInputComponent,
    FormInputGroupComponent,
    FormFileComponent,
    AppFileInputDirective,
    FormFilePathComponent,
    FormDateComponent,
    FormTimeComponent,
    FormTextareaComponent,
    FormCheckboxComponent,
    FormCheckboxListComponent,
    FormRadioListComponent,
    FormSelectComponent,
    FormFieldErrorComponent,
    FormLinkComponent,
    ModalComponent,
    ModalTemplateDirective,
    ToastComponent,
    LoadingComponent,
    RadioButtonsComponent,
    RadioListComponent,
    TooltipComponent,
    // Directives
    GreaterThanValidatorDirective,
    CurrencyMaskDirective,
    // Pipes
    SafePipe,
    GroupByPipe,
    YesNoPipe,
  ],
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    CurrencyPipe,
    DatePipe,
    FormsService,
    YesNoPipe,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoadingInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: JwtInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    { provide: NgbDateParserFormatter, useClass: CustomDateFormatter },
    { provide: NgbDateAdapter, useClass: CustomNgbDateNativeUTCAdapter },
  ],
})
// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class MssLibModule {
  static forRoot(mssLibConfig: MssLibConfig): ModuleWithProviders<MssLibModule> {
    return {
      ngModule: MssLibModule,
      providers: [
        {
          provide: MSSLIB_CONFIG,
          useValue: mssLibConfig,
        },
      ],
    };
  }
}

// Public API's
export * from './animations';
export * from './components';
export * from './constants';
export * from './models';
export * from './helpers';
export * from './pipes';
export * from './services';
